import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useEffect,
} from "react";
import { Modal, Steps, Button, message, Tooltip } from "antd";
import VideoRenderer from "../player/renderers/VideoRenderer";
import ImageRenderer from "../player/renderers/ImageRenderer";
import WebRenderer from "../player/renderers/WebRenderer";
import WidgetRenderer from "../player/renderers/WidgetRenderer";
import HLSRenderer from "../player/renderers/HLSRenderer";
import VideoURLRenderer from "../player/renderers/VideoURLRenderer";
import SearchableCheckableList from "../views/SearchableCheckableList";
import Data from "../../data/data";
import DataHelper from "../../helpers/DataHelper";
import SearchableCheckableMediaList from "../views/SearchableCheckableMediaList";
import DurationSelector from "../views/DurationSelector";
import { THEME_PALLETE } from "../../Theme";
import StringHelper from "../../helpers/StringHelper";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const MediaReplacePopup = forwardRef((props, ref) => {
  const { parentProps } = props;
  const { requestServerGet, requestServerPost, usertoken, selectedBrandData } =
    parentProps || {};
  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).replaceMedia;

  const stepsConfig = [
    {
      title: t.stepSelectPlaylists,
      mode: "playlists",
      description: t.descSelectPlaylists,
      instruction: t.instrSelectPlaylists,
      multiple: true,
    },
    {
      title: t.stepSelectMedia,
      mode: "media",
      description: t.descSelectMedia,
      instruction: t.instrSelectMedia,
      multiple: false,
    },
    {
      title: t.stepOverrideDuration,
      mode: "duration",
      description: t.descOverrideDuration,
      instruction: t.instrOverrideDuration,
      multiple: false,
    },
  ];
  const [current, setCurrent] = useState(0);
  const [playlistsData, setPlaylistsData] = useState([]);
  const [filesData, setFilesData] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMediaData, setPreviewMediaData] = useState(null);
  const [selectedPlaylists, setSelectedPlaylists] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [overrideDuration, setOverrideDuration] = useState(null); // Default duration in seconds

  const handlePlaylistsSelected = useCallback((items) => {
    setSelectedPlaylists((prev) => {
      const prevIds = Array.isArray(prev) ? prev.map((i) => i.id) : [];
      const newIds = Array.isArray(items) ? items.map((i) => i.id) : [];
      const changed =
        prevIds.length !== newIds.length ||
        !prevIds.every((id, i) => id === newIds[i]);

      console.log(`handlePlaylistsSelected changed: ${changed}`, items);
      if (changed) {
        console.log(`Updating selectedPlaylists`, items);
        return items;
      } else {
        return prev;
      }
    });
  }, []);

  const handleFileSelected = useCallback((items) => {
    console.log(`handleFileSelected: ${JSON.stringify(items)}`);
    setSelectedFiles(items ? (Array.isArray(items) ? items : [items]) : []);
  }, []);

  const handleDurationChange = ({ mode, duration }) => {
    console.log("Updated Duration:", mode, duration);

    if (mode == "custom") {
      setOverrideDuration(duration);
    } else {
      setOverrideDuration(null); // Reset to default if custom
    }
    // handle state update or send to API
  };

  const next = () => setCurrent(current + 1);
  const prev = () => setCurrent(current - 1);

  const showPreviewMedia = (data = null, doShow = true) => {
    return new Promise((resolve) => {
      setPreviewMediaData(data);
      setPreviewVisible(doShow);
      setPlaylistsData([]);
      resolve();
    });
  };

  const closePreview = () => showPreviewMedia(null, false);

  const initReplaceMedia = (mediaData) => {
    setCurrent(0);
    setSelectedPlaylists([]);
    setSelectedFiles([]);
    setOverrideDuration(null);
    // setPreviewVisible(false);
    // setPreviewMediaData(mediaData);
    showPreviewMedia(mediaData);
    loadFilesPlaylists(mediaData);
    loadFiles();
  };

  // useEffect(() => {
  //     if (parentProps && parentProps.selectedBrandData) {
  //         loadFiles();
  //     }
  // }, [selectedBrandData]);

  const loadFiles = () => {
    if (!usertoken) return;

    console.log("loadFiles for brand: " + selectedBrandData?.BrandID);
    if (!selectedBrandData) return;

    DataHelper.getFiles({ brandid: selectedBrandData.BrandID }, parentProps)
      .then((data) => {
        console.log("getFiles " + JSON.stringify(data));
        setFilesData(data.sort((a, b) => b.FileID - a.FileID));
      })
      .catch((error) => {
        console.error("Error fetching files:", error);
        message.error(t.errorLoadFiles);
      });
  };
  // requestServerGet(
  //     'get_files',
  //     {},
  //     null,
  //     (data) => {
  //         if (data?.data?.data) {
  //             setFilesData(data.data.data);
  //         }
  //     },
  //     usertoken
  // );
  // };
  const loadFilesPlaylists = (mediaData) => {
    if (!usertoken) return;
    requestServerGet(
      "get_playlists_by_fileid",
      { fileid: mediaData.FileID },
      null,
      (data) => {
        if (data?.data?.data) {
          setPlaylistsData(data.data.data);
        }
      },
      usertoken,
    );
  };

  useImperativeHandle(ref, () => ({
    initReplaceMedia,
  }));

  const currentContent = previewMediaData;
  if (!currentContent) return null;

  const {
    FileType = "",
    MediaType = "File",
    FileTypeSub = "",
  } = currentContent;
  const type = FileType.toLowerCase();
  const mediaType = type.split("/")[0];
  const mediaTypeSub = FileTypeSub.toLowerCase();

  let renderMedia = null;
  switch (MediaType) {
    case "Widget":
      renderMedia = <WidgetRenderer data={currentContent} />;
      break;
    default:
      switch (mediaType) {
        case "image":
          renderMedia = <ImageRenderer data={currentContent} />;
          break;
        case "video":
          renderMedia = <VideoRenderer data={currentContent} />;
          break;
        case "web":
          renderMedia = <WebRenderer data={currentContent} />;
          break;
        case "videourl":
          renderMedia = <VideoURLRenderer data={currentContent} />;
          break;
        case "streamingurl":
          if (mediaTypeSub === "hls") {
            renderMedia = <HLSRenderer src={currentContent.UploadFile} />;
          }
          break;
        default:
          break;
      }
  }

  const handleReplaceMedia = () => {
    if (selectedPlaylists.length === 0 || selectedFiles.length === 0) {
      message.error(t.errorSelectBoth);
      return;
    }
    const params = {
      oldfileid: currentContent.FileID,
      playlistids: selectedPlaylists.map((p) => p.id).join(",") || "",
      newfileid: selectedFiles[0].id,
      durations: overrideDuration,
    };
    if (!usertoken) return;
    requestServerPost(
      "replace_file_in_playlists",
      params,
      null,
      (data) => {
        console.log(
          "replace_file_in_playlists response: ",
          JSON.stringify(data),
        );
        if (
          "data" in data &&
          "status" in data.data &&
          data.data.status === "success"
        ) {
          message.success(t.successReplaced);
          closePreview();
        }
      },
      usertoken,
    );
  };

  const getStepContent = () => {
    const step = stepsConfig[current];

    switch (step.mode) {
      case "playlists":
        const transformedData = playlistsData.map((p) => ({
          id: p.PlaylistID,
          title: p.PlaylistName,
        }));

        return (
          <SearchableCheckableList
            headerTitle={t.headerPlaylists}
            key={`${step.mode}`}
            data={transformedData}
            multiple={step.multiple}
            onItemsSelected={handlePlaylistsSelected}
            values={selectedPlaylists.map((p) => p.id)}
          />
        );
        break;
      case "media":
        const transformedFilesData = filesData.map((p) => ({
          id: p.FileID,
          title: p.FileName,
          type: p.FileType,
          url: p.UploadFile,
        }));

        return (
          <SearchableCheckableMediaList
            headerTitle={t.headerMedia}
            key={`${step.mode}`}
            data={transformedFilesData}
            multiple={step.multiple}
            onItemsSelected={handleFileSelected}
            values={selectedFiles.map((f) => f.id)}
          />
        );
        break;
      case "duration":
        var duration = 15;

        if (
          selectedFiles &&
          selectedFiles.length > 0 &&
          selectedFiles[0].type === "Video"
        ) {
          var matchedFile = filesData.find(
            (file) => file.FileID === selectedFiles[0].id,
          );
          if (matchedFile) {
            const properties = matchedFile.FileProperties || {};
            try {
              const parsedProperties = JSON.parse(properties);
              duration = parseInt(parsedProperties.duration) || 15;
            } catch (e) {
              console.error("Error parsing duration: ", e);
            }
          }
        }
        return (
          <DurationSelector
            defaultDuration={duration}
            onChange={handleDurationChange}
            value={{
              mode: overrideDuration ? "custom" : "default",
              duration: overrideDuration,
            }}
          />
        );
        break;
      default:
        return <div>Unknown step</div>;
        break;
    }
  };

  const oldmedia = previewMediaData;
  const oldMediaPreview = oldmedia?.FileType?.startsWith("Image") ? (
    <img
      src={oldmedia?.UploadFile}
      alt={oldmedia?.FileName}
      style={{ maxWidth: 200, maxHeight: 120 }}
    />
  ) : oldmedia?.FileType?.startsWith("Video") ? (
    <video
      src={oldmedia?.UploadFile}
      muted
      autoPlay
      loop
      style={{ maxWidth: 200, maxHeight: 120 }}
    />
  ) : (
    <div>{oldmedia?.FileName}</div>
  );
  const newmedia = selectedFiles[0];
  const newMediaPreview = newmedia?.type?.startsWith("Image") ? (
    <img
      src={newmedia?.url}
      alt={newmedia?.title}
      style={{ maxWidth: 200, maxHeight: 120 }}
    />
  ) : newmedia?.type?.startsWith("Video") ? (
    <video
      src={newmedia?.url}
      muted
      autoPlay
      loop
      style={{ maxWidth: 200, maxHeight: 120 }}
    />
  ) : (
    <div>{newmedia?.title}</div>
  );

  return (
    <Modal
      className="dark-modal"
      title=""
      centered
      open={previewVisible}
      footer={null}
      onOk={closePreview}
      onCancel={closePreview}
      wrapClassName="modal-window"
      width={800}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "600px",
            display: "flex",
            flexDirection: "column",
            padding: "2em 0 1em 0",
          }}
        >
          <div style={{ marginBottom: "4px" }}>
            <h2 style={{ margin: 0 }}>{t.title}</h2>
          </div>
          <div style={{ margin: "4px 0 2em 0", width: "100%", color: "#666" }}>
            {stepsConfig[current].description}
          </div>
          <Steps
            current={current}
            items={stepsConfig.map((s) => ({ key: s.title, title: s.title }))}
          />
          <div
            className="steps-content"
            style={{ flexGrow: 1, padding: 24, width: "100%" }}
          >
            {getStepContent()}
          </div>
          <div
            className="steps-action"
            style={{
              marginTop: 16,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* {selectedFiles.length > 0 && (
                            <Tooltip
                                title={selectedFiles[0]?.title || ''}
                                placement="top"
                            >
                                <div style={{ marginBottom: 8, color: '#555' }}>
                                    Replace with media: {selectedFiles[0]?.title}
                                </div>
                            </Tooltip>
                        )} */}
            <div
              style={{
                marginTop: 4,
                marginBottom: 12,
                color: "#444",
                textAlign: "center",
                fontSize: "15px",
              }}
            >
              {selectedPlaylists.length > 0 && selectedFiles.length > 0 ? (
                <>
                  {t.statusWith.trimStart()}&nbsp;
                  <Tooltip title={oldMediaPreview}>
                    <strong
                      style={{ cursor: "pointer", color: THEME_PALLETE.blue }}
                    >
                      {previewMediaData?.FileName}
                    </strong>
                  </Tooltip>
                  {t.statusWith}
                  <Tooltip title={newMediaPreview}>
                    <strong
                      style={{ cursor: "pointer", color: THEME_PALLETE.blue }}
                    >
                      {selectedFiles[0]?.title}
                    </strong>
                  </Tooltip>
                  {t.statusIn}
                  <Tooltip
                    title={
                      <div>
                        {selectedPlaylists.map((p) => (
                          <div>{p.title}</div>
                        ))}
                      </div>
                    }
                    placement="top"
                  >
                    <strong
                      style={{ cursor: "pointer", color: THEME_PALLETE.purple }}
                    >
                      {selectedPlaylists.length}
                    </strong>
                  </Tooltip>
                  {t.statusPlaylist(selectedPlaylists.length)}
                  {overrideDuration ? t.statusCustomDuration : ""}
                  &nbsp;
                  <strong style={{ color: THEME_PALLETE.green }}>
                    {overrideDuration
                      ? `${StringHelper.formatDuration(overrideDuration)}`
                      : " "}
                  </strong>
                </>
              ) : selectedPlaylists.length > 0 ? (
                <>{t.statusSelectMedia}</>
              ) : selectedFiles.length > 0 ? (
                <>{t.statusSelectPlaylists}</>
              ) : (
                <>{t.statusBegin}</>
              )}
            </div>

            {/* ✅ Navigation Buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {current > 0 && (
                <Button style={{ marginRight: 8 }} onClick={prev}>
                  {t.previous}
                </Button>
              )}
              {current < stepsConfig.length - 1 && (
                <Button
                  type="primary"
                  onClick={next}
                  disabled={
                    (current === 0 && selectedPlaylists.length === 0) ||
                    (current === 1 && selectedFiles.length === 0)
                  }
                >
                  {t.next}
                </Button>
              )}
              {current === stepsConfig.length - 1 && (
                <Button
                  type="primary"
                  onClick={() => {
                    Modal.confirm({
                      title: t.confirmTitle,
                      content: (
                        <>
                          <p>
                            {t.confirmContent(
                              previewMediaData?.FileName,
                              selectedFiles[0]?.title,
                              selectedPlaylists.length,
                            )}
                          </p>
                          {overrideDuration && (
                            <p>
                              {t.confirmDuration(
                                StringHelper.formatDuration(overrideDuration),
                              )}
                            </p>
                          )}
                        </>
                      ),
                      okText: t.confirmOk,
                      cancelText: t.confirmCancel,
                      centered: true,
                      onOk: handleReplaceMedia,
                    });
                  }}
                >
                  {t.done}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
});

export default MediaReplacePopup;
